import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

/**
 * /verify-email?token=<raw>
 *
 * Reads the token from the URL, POSTs to /auth/verify-email, and shows the
 * outcome. On success we bounce to the dashboard after 3s. Users hitting this
 * page without a token get a friendly explanation instead of a crash.
 */
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "loading" : "missing");
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        setEmail(data?.email || "");
        setState("success");
        setTimeout(() => { window.location.href = "/dashboard"; }, 3000);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Verification failed. Please try again.");
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]" data-testid="verify-email-page">
      <Header />
      <main className="flex-1 flex items-center justify-center py-12">
        <div className="w-full max-w-md mx-auto bg-white border border-[#e2e8f0] rounded-xl p-8 text-center shadow-sm">
          {state === "loading" && (
            <>
              <Loader2 className="w-12 h-12 text-[#2563eb] mx-auto mb-4 animate-spin" data-testid="verify-loading" />
              <h1 className="text-xl font-display font-bold text-[#0f1b2d]">Verifying your email…</h1>
              <p className="mt-2 text-sm text-[#475569]">Hang tight, this only takes a second.</p>
            </>
          )}
          {state === "success" && (
            <>
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" data-testid="verify-success" />
              <h1 className="text-xl font-display font-bold text-[#0f1b2d]">Email verified</h1>
              <p className="mt-2 text-sm text-[#475569]">
                {email ? <><span className="font-medium text-[#0f1b2d]">{email}</span> is now confirmed. </> : null}
                Redirecting you to your dashboard…
              </p>
              <Link to="/dashboard" className="mt-6 inline-block text-sm font-semibold text-[#2563eb] hover:underline">Go to dashboard →</Link>
            </>
          )}
          {state === "error" && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" data-testid="verify-error" />
              <h1 className="text-xl font-display font-bold text-[#0f1b2d]">Verification failed</h1>
              <p className="mt-2 text-sm text-[#475569]">{error}</p>
              <Link to="/dashboard" className="mt-6 inline-block text-sm font-semibold text-[#2563eb] hover:underline">
                Sign in and request a new link →
              </Link>
            </>
          )}
          {state === "missing" && (
            <>
              <XCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" data-testid="verify-missing" />
              <h1 className="text-xl font-display font-bold text-[#0f1b2d]">No verification token</h1>
              <p className="mt-2 text-sm text-[#475569]">
                Please click the verification link from your welcome email. If you can&apos;t find it, sign in and use the &quot;Resend&quot; button in your dashboard.
              </p>
              <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-[#2563eb] hover:underline">Sign in →</Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
